import React, { useState } from 'react';
import { Share2, Printer, Copy, Check, X } from 'lucide-react';

const SharePrintButtons = ({ 
  item, 
  itemType, 
  className = "",
  showLabels = false,
  size = "default" // "small", "default", "large"
}) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const getItemUrl = () => {
    const baseUrl = window.location.origin;
    switch (itemType) {
      case 'product':
        return `${baseUrl}/product/${item.slug}`;
      case 'service':
        return `${baseUrl}/services#${item.slug}`;
      case 'project':
        return `${baseUrl}/portfolio#${item.slug}`;
      case 'blog':
        return `${baseUrl}/blog#${item.slug}`;
      default:
        return window.location.href;
    }
  };

  const getItemTitle = () => {
    return item.name || item.title || 'Item';
  };

  const getItemDescription = () => {
    return item.description || item.excerpt || '';
  };

  const handleShare = async (platform) => {
    const url = getItemUrl();
    const title = getItemTitle();
    const description = getItemDescription();

    try {
      switch (platform) {
        case 'copy':
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          break;
        
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
          break;
        
        case 'twitter':
          const twitterText = `${title} - ${description}`.substring(0, 100);
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(url)}`, '_blank');
          break;
        
        case 'linkedin':
          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
          break;
        
        case 'whatsapp':
          const whatsappText = `${title} - ${url}`;
          window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, '_blank');
          break;
        
        case 'email':
          const emailSubject = `Check out this ${itemType}: ${title}`;
          const emailBody = `I thought you might be interested in this ${itemType}:\n\n${title}\n\n${description}\n\n${url}`;
          window.open(`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`);
          break;
        
        default:
          if (navigator.share) {
            await navigator.share({
              title: title,
              text: description,
              url: url
            });
          } else {
            // Fallback to copy
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
    
    setShowShareMenu(false);
  };

  const handlePrint = () => {
    // Create a print-friendly version of the page
    const printWindow = window.open('', '_blank');
    const itemUrl = getItemUrl();
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${getItemTitle()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .print-header { text-align: center; margin-bottom: 30px; }
            .print-content { max-width: 800px; margin: 0 auto; }
            .print-image { max-width: 100%; height: auto; margin: 20px 0; }
            .print-description { line-height: 1.6; margin: 20px 0; }
            .print-footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="print-content">
            <div class="print-header">
              <h1>${getItemTitle()}</h1>
              <p>${getItemDescription()}</p>
            </div>
            
            ${item.image ? `<img src="${item.image}" alt="${getItemTitle()}" class="print-image">` : ''}
            
            <div class="print-description">
              ${item.description || item.excerpt || ''}
            </div>
            
            <div class="print-footer">
              <p>Shared from ${window.location.origin}</p>
              <p>Original URL: ${itemUrl}</p>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-6 h-6';
      case 'large':
        return 'w-8 h-8';
      default:
        return 'w-7 h-7';
    }
  };

  const getButtonClasses = () => {
    const baseClasses = "flex items-center justify-center rounded-full transition-all duration-200 hover:scale-110";
    const sizeClasses = getSizeClasses();
    
    switch (size) {
      case 'small':
        return `${baseClasses} ${sizeClasses} bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300`;
      case 'large':
        return `${baseClasses} ${sizeClasses} bg-primary-100 hover:bg-primary-200 dark:bg-primary-900 dark:hover:bg-primary-800 text-primary-600 dark:text-primary-400`;
      default:
        return `${baseClasses} ${sizeClasses} bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300`;
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Share Button */}
      <div className="relative">
        <button
          onClick={() => setShowShareMenu(!showShareMenu)}
          className={getButtonClasses()}
          title="Share"
        >
          <div className="flex items-center justify-center">
            <Share2 className="w-4 h-4" />
            {showLabels && <span className="ml-1 text-xs">Share</span>}
          </div>
        </button>

        {/* Share Menu */}
        {showShareMenu && (
          <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-50 min-w-[200px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Share</span>
              <button
                onClick={() => setShowShareMenu(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-1">
              <button
                onClick={() => handleShare('copy')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              
              <button
                onClick={() => handleShare('facebook')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              
              <button
                onClick={() => handleShare('twitter')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </button>
              
              <button
                onClick={() => handleShare('linkedin')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </button>
              
              <button
                onClick={() => handleShare('whatsapp')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>
              
              <button
                onClick={() => handleShare('email')}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Print Button */}
      <button
        onClick={handlePrint}
        className={getButtonClasses()}
        title="Print"
      >
        <div className="flex items-center justify-center">
          <Printer className="w-4 h-4" />
          {showLabels && <span className="ml-1 text-xs">Print</span>}
        </div>
      </button>
    </div>
  );
};

export default SharePrintButtons; 